import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';
import { I18nService } from '../../../../core/i18n';
import { CompanyContextService } from '../../../../core/tenant/company-context.service';
import { TPipe } from '../../../../shared/pipes';
import { AppModalComponent, ToastService } from '../../../../shared/ui';
import type { OnboardingChecklist } from '../../models/onboarding-checklist.model';
import { OnboardingChecklistStore } from '../../stores/onboarding-checklist.store';
import { ChecklistItemComponent } from './checklist-item.component';

interface ChecklistItemDescriptor {
  key: 'createProject' | 'createProcess' | 'createCard' | 'moveCard' | 'commentCard' | 'createAutomation';
  flagKey: keyof OnboardingChecklist;
  iconName: string;
  pathSuffix: ReadonlyArray<string>;
}

const CHECKLIST_ITEMS: ReadonlyArray<ChecklistItemDescriptor> = [
  { key: 'createProject', flagKey: 'firstProjectCreated', iconName: 'briefcase', pathSuffix: ['projects', 'new'] },
  { key: 'createProcess', flagKey: 'firstProcessCreated', iconName: 'workflow', pathSuffix: ['projects'] },
  { key: 'createCard', flagKey: 'firstCardCreated', iconName: 'layout-grid', pathSuffix: ['projects'] },
  { key: 'moveCard', flagKey: 'firstCardMoved', iconName: 'move-horizontal', pathSuffix: ['projects'] },
  { key: 'commentCard', flagKey: 'firstCommentAdded', iconName: 'message-square-plus', pathSuffix: ['projects'] },
  { key: 'createAutomation', flagKey: 'firstAutomationCreated', iconName: 'zap', pathSuffix: ['projects'] },
];

@Component({
  selector: 'app-onboarding-checklist',
  standalone: true,
  imports: [CommonModule, LucideAngularModule, TPipe, ChecklistItemComponent, AppModalComponent],
  templateUrl: './onboarding-checklist.component.html',
  styles: [
    `
      .confirm-body {
        padding: 4px 0 8px;
      }

      .confirm-text {
        margin: 0;
        font-size: 13px;
        color: var(--text);
        line-height: 1.5;
      }

      .confirm-footer {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 8px;
      }

      .confirm-cancel-btn,
      .confirm-primary-btn {
        height: 36px;
        border-radius: 9px;
        padding: 0 14px;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }

      .confirm-cancel-btn {
        background: var(--bg-surface);
        border: 1px solid var(--border);
        color: var(--text);
      }

      .confirm-primary-btn {
        background: #111827;
        border: 1px solid #111827;
        color: #fff;
      }

      .confirm-cancel-btn:disabled,
      .confirm-primary-btn:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      .confirm-spin {
        animation: confirm-spin 0.9s linear infinite;
      }

      @keyframes confirm-spin {
        to {
          transform: rotate(360deg);
        }
      }
    `,
  ],
})
export class OnboardingChecklistComponent {
  protected readonly store = inject(OnboardingChecklistStore);
  private readonly companyContext = inject(CompanyContextService);
  private readonly i18n = inject(I18nService);
  private readonly toast = inject(ToastService);

  protected readonly items = CHECKLIST_ITEMS;
  protected readonly progressPercent = computed(
    () => (this.store.completedCount() / this.store.total) * 100
  );

  protected readonly showConfirmModal = signal(false);
  protected readonly dismissing = signal(false);

  constructor() {
    let previousKey: string | null = null;
    effect(() => {
      const key = this.companyContext.activeCompanyKey();
      if (!key) return;
      if (previousKey !== null && previousKey !== key) {
        this.store.resetSession();
      }
      previousKey = key;
      void this.store.refresh();
    });
  }

  protected isItemDone(flagKey: keyof OnboardingChecklist): boolean {
    const checklist = this.store.checklist();
    if (!checklist) return false;
    return checklist[flagKey] === true;
  }

  protected getRoute(item: ChecklistItemDescriptor): readonly unknown[] | null {
    const key = this.companyContext.activeCompanyKey();
    return key ? ['/c', key, ...item.pathSuffix] : null;
  }

  protected onAlreadyDoneClick(): void {
    this.showConfirmModal.set(true);
  }

  protected onCancelDismiss(): void {
    if (this.dismissing()) return;
    this.showConfirmModal.set(false);
  }

  protected async onConfirmDismiss(): Promise<void> {
    if (this.dismissing()) return;
    this.dismissing.set(true);
    try {
      await this.store.dismiss();
      this.showConfirmModal.set(false);
      this.toast.success(this.i18n.t('onboardingChecklist.dismissedToast'));
    } catch {
      this.toast.show(this.i18n.t('onboardingChecklist.dismissErrorToast'), 'error');
    } finally {
      this.dismissing.set(false);
    }
  }
}
